import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import Swal from 'sweetalert2';
import { environment } from '../../environments/environment';
import { ShellService } from '../shell/shell.service';
import { InspectionFilters, InspectionListAccess, InspectionListService, InspectionRow } from './inspection-list.service';

const PAGE_SIZES = [25, 50, 100];

// Twin-ci's defaults: All Active, not deleted, every type and date, newest scheduled first.
function defaultFilters(): InspectionFilters {
    return {
        status: 'active', search: '', agent_id: 0, branch_id: 0, inspection_type: '', date_from: '', date_to: '',
        property_address: '', include_deleted: false, include_cancelled: false, include_on_hold: false,
        sort: '', dir: 'desc'
    };
}

/**
 * Inspection Orders list (Inspectmite redesign, mockup inspection-orders), replacing twin-ci's
 * inspections_list pages and export. Which jobs appear and which actions are offered come from
 * the API per role and permission (InspectionListService / InspectionListController); the page
 * only hides what the API says the user can't do, the API enforces it.
 */
@Component({
    selector: 'app-inspection-list',
    templateUrl: './inspection-list.component.html',
    styleUrls: ['./inspection-list.component.scss']
})
export class InspectionListComponent implements OnInit, OnDestroy {
    readonly pageSizes = PAGE_SIZES;
    readonly statuses = [
        { value: 'active', label: 'All Active' },
        { value: 'all', label: 'All' },
        // Mockup labels; the values stay the API's status filters.
        { value: 'Active - Intake', label: 'Intake' },
        { value: 'Active - Inspection', label: 'Inspection' },
        { value: 'Active - Work', label: 'Work' },
        { value: 'Completed', label: 'Completed' },
        { value: 'Hold', label: 'Hold' },
        { value: 'Cancelled', label: 'Cancelled' }
    ];
    // Values are the stored inspection types; labels as in the mockup's advanced search.
    readonly types = [
        { value: 'Original', label: 'Original Inspection' },
        { value: 'Limited', label: 'Limited Inspection' },
        { value: 'Supplemental', label: 'Supplemental Inspection' },
        { value: 'Reinspection', label: 'Reinspection' }
    ];
    readonly columns = [
        { key: 'report', label: 'Report #' }, { key: 'type', label: 'Type' }, { key: 'property', label: 'Property' },
        { key: 'client', label: 'Client' }, { key: 'status', label: 'Status' }, { key: 'scheduled', label: 'Scheduled' },
        { key: 'inspector', label: 'Inspector' }
    ];

    access: InspectionListAccess = null;
    state: 'loading' | 'ready' | 'error' | 'denied' = 'loading';
    filters = defaultFilters();
    showAdvanced = false;
    rows: InspectionRow[] = [];
    total = 0;
    page = 1;
    perPage = 25;
    exporting = false;
    createRoute = '';

    private searchInput = new Subject<string>();
    private subs: Subscription[] = [];
    private request: Subscription;

    constructor(private api: InspectionListService, private shell: ShellService, private route: ActivatedRoute) { }

    ngOnInit() {
        // "See More Results" from the global search lands here with ?q= (all statuses, like twin-ci).
        // Followed live, because a new search while already on this page reuses the component.
        this.subs.push(this.route.queryParamMap.subscribe(params => {
            const q = params.get('q');
            if (q && q !== this.filters.search) {
                this.filters.search = q;
                this.filters.status = 'all';
                if (this.access) {
                    this.reload();
                }
            }
        }));
        const savedSize = this.shell.get('inspections_per_page', 25);
        this.perPage = PAGE_SIZES.indexOf(savedSize) !== -1 ? savedSize : 25;

        this.subs.push(this.searchInput.pipe(debounceTime(300)).subscribe(() => this.reload()));
        this.subs.push(this.shell.settings$.subscribe(settings => {
            // Same rule as the sidebar's Create New Inspection link.
            this.createRoute = settings.inspection_tab_order == 1 ? '/inspections-edit-property' : '/inspections-order-parties';
        }));
        this.api.access().subscribe(access => {
            this.access = access;
            this.load();
        }, err => this.state = err && err.status === 403 ? 'denied' : 'error');
    }

    ngOnDestroy() {
        this.subs.forEach(s => s.unsubscribe());
        if (this.request) {
            this.request.unsubscribe();
        }
    }

    // ---- Loading ----

    reload() {
        this.page = 1;
        this.load();
    }

    load() {
        this.state = this.rows.length ? this.state : 'loading';
        if (this.request) {
            this.request.unsubscribe();
        }
        this.request = this.api.list(this.filters, this.page, this.perPage).subscribe(res => {
            this.rows = res.rows;
            this.total = res.total;
            this.state = 'ready';
        }, err => this.state = err && err.status === 403 ? 'denied' : 'error');
    }

    onSearch() {
        this.searchInput.next(this.filters.search);
    }

    setPageSize(size: number) {
        this.perPage = size;
        this.shell.set('inspections_per_page', size);   // remembered per user
        this.reload();
    }

    sortBy(key: string) {
        if (this.filters.sort === key) {
            this.filters.dir = this.filters.dir === 'asc' ? 'desc' : 'asc';
        } else {
            this.filters.sort = key;
            this.filters.dir = key === 'scheduled' ? 'desc' : 'asc';
        }
        this.reload();
    }

    sortState(key: string): string {
        if (this.filters.sort !== key && !(key === 'scheduled' && !this.filters.sort)) {
            return 'none';
        }
        return this.filters.dir === 'asc' ? 'ascending' : 'descending';
    }

    clearFilters() {
        const keepSize = this.perPage;
        this.filters = defaultFilters();
        this.perPage = keepSize;
        this.reload();
    }

    get activeAdvanced(): number {
        const f = this.filters;
        return [f.date_from, f.date_to, f.branch_id, f.inspection_type, f.property_address, f.include_deleted, f.include_cancelled, f.include_on_hold]
            .filter(v => !!v).length;
    }

    // ---- Paging ----

    get pages(): number {
        return Math.max(1, Math.ceil(this.total / this.perPage));
    }

    get pageNumbers(): number[] {
        const last = this.pages;
        const from = Math.max(1, Math.min(this.page - 2, last - 4));
        const to = Math.min(last, from + 4);
        const list: number[] = [];
        for (let p = from; p <= to; p++) {
            list.push(p);
        }
        return list;
    }

    goTo(page: number) {
        if (page >= 1 && page <= this.pages && page !== this.page) {
            this.page = page;
            this.load();
        }
    }

    get rangeLabel(): string {
        if (!this.total) {
            return 'No inspection orders';
        }
        const from = (this.page - 1) * this.perPage + 1;
        const to = Math.min(this.total, this.page * this.perPage);
        return 'Showing ' + from + '–' + to + ' of ' + this.total + ' inspection orders';
    }

    // ---- Row actions ----

    printUrl(row: InspectionRow): string {
        return environment.apiSite + 'print-pdf/field-sheet/' + row.job_id;
    }

    // Inspectors keep twin-ci's transactions page; everyone else the Angular one, as before.
    transactionsHref(row: InspectionRow): string {
        return this.access.role === 'inspector' ? environment.apiSite + 'url/accounting_transactions_list/' + row.job_id : null;
    }

    confirmDelete(row: InspectionRow) {
        Swal({
            title: 'Delete report #' + row.report_number + '?',
            text: 'Are you sure you want to permanently delete the record for this Inspection/Job/Order?',
            type: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Delete',
            confirmButtonColor: '#a52c2c'
        }).then(result => {
            if (result.value) {
                this.api.delete(row.job_id).subscribe(() => this.load(), () => {
                    Swal({ type: 'error', title: 'Not deleted', text: 'The record could not be deleted.' });
                });
            }
        });
    }

    // ---- Export ----

    exportCsv() {
        this.exporting = true;
        this.api.export(this.filters).subscribe(res => {
            const disposition = res.headers.get('Content-Disposition') || '';
            const match = /filename="?([^";]+)"?/.exec(disposition);
            const url = URL.createObjectURL(res.body);
            const link = document.createElement('a');
            link.href = url;
            link.download = match ? match[1] : 'inspection-orders.csv';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            setTimeout(() => URL.revokeObjectURL(url), 1000);
            this.exporting = false;
        }, () => {
            this.exporting = false;
            Swal({ type: 'error', title: 'Export failed', text: 'The export could not be created. Please try again.' });
        });
    }

    trackByJob(index: number, row: InspectionRow) {
        return row.job_id;
    }
}
